import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';

import {
	Button,
	Confirm,
	CustomTooltip,
	Icons,
	Modal,
	SliderCheckbox,
	TextClamp,
} from '@components/common';
import Actions from '@components/common/Actions';
import ProfileTabSection from '@components/common/ProfileTabSection';
import UsersTooltip from '@components/common/UsersTooltip';

import { useMutatePortalProjects } from '@hooks/portal/projects/portalProjects/useMutatePortalProjects';
import { useQueryUser } from '@hooks/useQueryUser';
import { useSearchParamsQuery } from '@hooks/useSearchParamsQuery';

import { IProjectFormValues } from '@interfaces/components/form';
import { IEditProjectDTO, IProjectDTO } from '@interfaces/portal/dto/projectManagementDTO';

import { MODAL_WIDTH_480, MODAL_WIDTH_580, modalCloseConfirm } from '@constants/modals';
import {
	backPagePath,
	editIconDisabledTooltip,
	editIconTooltip,
	getTabs,
	portalProjectStatuses,
	portalSearchQueries,
	tabsNames,
} from './constants';

import EditPortalProjectForm from './EditPortalProjectForm';
import Files from './Files';
import Milestones from './Milestones';
import Tasks from './Tasks';
import Timeline from './Timeline';

import styles from './ProjectProfile.module.scss';

interface IProjectProfileProps {
	project: IProjectDTO;
	organisationId: string;
}

const ProjectProfile = ({
	project,
	organisationId,
}: IProjectProfileProps) => {
	const { user } = useQueryUser();

	const navigate = useNavigate();
	const query = useSearchParamsQuery();

	const queryMilestoneId = query.get(portalSearchQueries.milestone);
	const queryTaskId = query.get(portalSearchQueries.task);
	const isQueryFolderId = query.has(portalSearchQueries.folder);

	const onBackClickHandler = () => navigate(backPagePath);

	const isProjectActive = project?.status.name === portalProjectStatuses.active;
	const isProjectAdmin = project?.admins?.some(admin => admin.id === user?.id);

	const [isEditProjectFormModal, setIsEditProjectFormModal] = useState(false);
	const [isCloseConfirmModal, setIsCloseConfirmModal] = useState(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [currentTab, setCurrentTab] = useState(tabsNames.tasks);

	useEffect(() => {
		if (isQueryFolderId) {
			setCurrentTab(tabsNames.files);
		} else if (queryMilestoneId) {
			setCurrentTab(tabsNames.milestones);
		} else if (queryTaskId) {
			setCurrentTab(tabsNames.tasks);
		}
	}, [queryMilestoneId, queryTaskId, isQueryFolderId]);

	const openEditProjectModal = () => setIsEditProjectFormModal(true);

	const closeModals = () => {
		setIsEditProjectFormModal(false);
		setIsCloseConfirmModal(false);
	};

	const toggleCloseConfirmModal = () => {
		if (isDirtyFormModal) setIsCloseConfirmModal(prevState => !prevState);
		else closeModals();
	};

	const { updateProjectStatus } = useMutatePortalProjects();
	const { updatePortalProject } = useMutatePortalProjects();

	const onChangeStatus = () => {
		if (!project?.id || !project.status) return;

		void updateProjectStatus({
			projectId: project.id,
			dto: {
				status: project.status.name === portalProjectStatuses.active
					? portalProjectStatuses.inactive
					: portalProjectStatuses.active,
			},
		});
	};

	const submitForm = useCallback((values: IProjectFormValues) => {
		if (!organisationId || !project?.id) return;

		const editSubmitData: IEditProjectDTO = {
			projectId: project.id,
			dto: {
				admins: values.admins?.map(admin => admin?.name as string) || [],
				members: values.members?.map(member => member?.name as string) || [],
				description: values.description,
				name: values.projectName,
				status: values.status?.value || '',
			},
		};

		void updatePortalProject(editSubmitData);

		closeModals();
	}, [
		organisationId,
		project?.id,
		updatePortalProject,
	]);

	const tabs = getTabs();

	const getCurrentTab = () => {
		switch (currentTab) {
			case tabsNames.tasks:
				return (
					<Tasks
						isPortalAdmin={isProjectAdmin}
						isProjectActive={isProjectActive}
						projectId={project.id}
					/>
				);

			case tabsNames.milestones:
				return (
					<Milestones
						isPortalAdmin={isProjectAdmin}
						isProjectActive={isProjectActive}
						projectId={project.id}
					/>
				);

			case tabsNames.timeline:
				return (
					<Timeline
						isProjectActive={isProjectActive}
						projectId={project.id}
					/>
				);

			case tabsNames.files:
				return (
					<Files
						isPortalAdmin={isProjectAdmin}
						isProjectActive={isProjectActive}
						projectId={project.id}
					/>
				);

			default:
				return (
					<Tasks
						isPortalAdmin={isProjectAdmin}
						isProjectActive={isProjectActive}
						projectId={project?.id}
					/>
				);
		}
	};

	const isProjectEditable = isProjectAdmin && isProjectActive;

	return (
		<div className={styles.projectProfileContainer}>
			<Modal
				isOpen={isEditProjectFormModal}
				onClose={toggleCloseConfirmModal}
				title='Edit project'
				width={MODAL_WIDTH_580}
			>
				<EditPortalProjectForm
					editableProject={project}
					organisationId={organisationId}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitForm={submitForm}
					toggleCloseConfirmModal={toggleCloseConfirmModal}
				/>
			</Modal>
			<Modal
				isOpen={isCloseConfirmModal}
				onClose={toggleCloseConfirmModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={closeModals}
					{...modalCloseConfirm}
				/>
			</Modal>
			<Button
				icon={Icons.arrowLeft()}
				onClick={onBackClickHandler}
				text='Back'
				withoutBackground
			/>
			<div className={styles.header}>
				<div className={cn(styles.whiteWrapper, styles.nameSection)}>
					<div className={styles.nameHeader}>
						<div className={styles.name}>
							{project?.name}
						</div>
						{isProjectAdmin
							&& <Actions
								editHandler={openEditProjectModal}
								editTooltip={isProjectEditable ? editIconTooltip : editIconDisabledTooltip}
								gridNumberOfColumns={1}
								isEdit
								isEditDisable={!isProjectEditable}
							/>
						}
					</div>
					<div className={styles.users}>
						{project?.admins
							&& <CustomTooltip
								interactive
								isDisabled={!project.admins.length}
								placement='top'
								tooltip={(
									<UsersTooltip
										users={project.admins}
									/>
								)}
							>
								<div className={styles.userCounter}>
									{`${project.admins.length} admin${project.admins.length !== 1 ? 's' : ''}`}
								</div>
							</CustomTooltip>
						}
						{project?.members
							&& <CustomTooltip
								interactive
								isDisabled={!project.members.length}
								placement='top'
								tooltip={(
									<UsersTooltip
										users={project.members}
									/>
								)}
							>
								<div className={styles.userCounter}>
									{`${project.members.length} member${project.members.length !== 1 ? 's' : ''}`}
								</div>
							</CustomTooltip>
						}
					</div>
					{project?.description
						&& <div className={styles.description}>
							<TextClamp
								fontSize={14}
								isToggleButtonLeft
								text={project.description}
								textName='Description'
							/>
						</div>
					}
				</div>
				<div className={cn(styles.whiteWrapper, styles.statusSection)}>
					{isProjectAdmin
						&& <div className={styles.checkbox}>
							<SliderCheckbox
								checked={project?.status.name === portalProjectStatuses.active}
								id='status'
								label='Project Status'
								onChange={onChangeStatus}
							/>
						</div>
					}
					<div className={styles.projectInfo}>
						<div className={styles.infoTitle}>Project Code:</div>
						<div className={styles.infoValue}>{project?.code}</div>
					</div>
					{!isProjectAdmin
						&& <div className={styles.projectInfo}>
							<div className={styles.infoTitle}>Project Status:</div>
							<div className={styles.infoValue}>{project?.status.title}</div>
						</div>
					}
					{project?.workflow
						&& <div className={styles.projectInfo}>
							<div className={styles.infoTitle}>Workflow:</div>
							<div className={styles.infoValue}>
								{project.workflow.title}
								<br />
								{`(${project.workflow.statuses})`}
							</div>
						</div>
					}
				</div>
			</div>
			<ProfileTabSection
				contentClassName={styles.profileTabSection}
				currentTab={currentTab}
				getCurrentTab={getCurrentTab}
				maxWidth='none'
				setCurrentTab={setCurrentTab}
				tabs={tabs}
			/>
		</div>
	);
};

export default ProjectProfile;
